/**
 * Helpers for coach review view_setup (rotation / flip / zoom / pan).
 * Stored on the feedback message and reapplied as CSS — not baked into the file.
 */

export function parseVideoViewSetup(raw) {
  if (!raw) return null;
  let data = raw;
  if (typeof raw === "string") {
    try {
      data = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== "object") return null;

  const rotation = Number(data.rotation_deg ?? data.rotationDeg ?? 0);
  const mod = ((Math.round(rotation) % 360) + 360) % 360;
  const rotationDeg =
    mod === 90 || mod === 180 || mod === 270 ? mod : 0;
  const zoomScale = Math.max(
    1,
    Math.min(5, Number(data.zoom_scale ?? data.zoomScale) || 1)
  );
  const panXRatio = Number(data.pan_x_ratio ?? data.panXRatio);
  const panYRatio = Number(data.pan_y_ratio ?? data.panYRatio);

  return {
    version: 1,
    rotationDeg,
    flipH: Boolean(data.flip_h ?? data.flipH),
    flipV: Boolean(data.flip_v ?? data.flipV),
    zoomScale,
    panXRatio: Number.isFinite(panXRatio) ? panXRatio : 0,
    panYRatio: Number.isFinite(panYRatio) ? panYRatio : 0,
  };
}

/** True when the saved view is not identity (needs gate before first paint). */
export function hasSavedVideoViewSetup(raw) {
  const parsed = typeof raw === "object" && raw && "rotationDeg" in raw
    ? raw
    : parseVideoViewSetup(raw);
  if (!parsed) return false;
  return (
    parsed.rotationDeg !== 0 ||
    Boolean(parsed.flipH) ||
    Boolean(parsed.flipV) ||
    parsed.zoomScale > 1.001 ||
    Math.abs(parsed.panXRatio) > 0.001 ||
    Math.abs(parsed.panYRatio) > 0.001
  );
}

/** Rotation or mirror only (zoom/pan alone do not qualify for orient bake). */
export function hasOrientVideoTransform(raw) {
  const parsed =
    typeof raw === "object" && raw && "rotationDeg" in raw
      ? raw
      : parseVideoViewSetup(raw);
  if (!parsed) return false;
  return (
    parsed.rotationDeg !== 0 ||
    Boolean(parsed.flipH) ||
    Boolean(parsed.flipV)
  );
}

export function buildVideoViewSetupPayload({
  rotationDeg = 0,
  flipH = false,
  flipV = false,
  zoomScale = 1,
  panX = 0,
  panY = 0,
  viewportW = 0,
  viewportH = 0,
} = {}) {
  const w = Math.max(1, Number(viewportW) || 1);
  const h = Math.max(1, Number(viewportH) || 1);
  return {
    version: 1,
    rotation_deg: rotationDeg,
    flip_h: Boolean(flipH),
    flip_v: Boolean(flipV),
    zoom_scale: Math.max(1, Number(zoomScale) || 1),
    pan_x_ratio: (Number(panX) || 0) / w,
    pan_y_ratio: (Number(panY) || 0) / h,
  };
}

/**
 * Rebase HR points so the first sample is at t=0 (matches video playhead).
 * @param {Array<{ time: number, hr_value?: number, hr?: number }>|null|undefined} points
 * @returns {Array<{ time: number, hr_value: number }>|null}
 */
export function rebaseHrPointsToClipStart(points) {
  if (!Array.isArray(points) || points.length === 0) return null;
  const valid = points
    .map((p) => ({
      time: Number(p.time),
      hr_value: Number(p.hr_value ?? p.hr),
    }))
    .filter((p) => Number.isFinite(p.time) && Number.isFinite(p.hr_value) && p.hr_value > 0)
    .sort((a, b) => a.time - b.time);
  if (!valid.length) return null;
  const t0 = valid[0].time;
  return valid.map((p) => ({
    time: p.time - t0,
    hr_value: p.hr_value,
  }));
}

/**
 * First name for HR overlay labels (falls back to alias / full name).
 */
export function formatHrOverlayFirstName(player, playerId) {
  if (player) {
    const first = String(
      player.user_first_name || player.first_name || ""
    ).trim();
    if (first) return first;
    const alias = String(player.user_alias || player.player_alias || "").trim();
    if (alias) return alias;
    const roster = String(player.player_name || player.name || "").trim();
    if (roster) return roster.split(/\s+/)[0] || roster;
  }
  if (playerId) return `Player ${String(playerId).slice(-6)}`;
  return null;
}

/**
 * HR bpm for a video playhead against a clip-aligned series (t≈0 at clip start).
 * Uses a single stable mapping so the value tracks the scrubber (no freeze / flicker).
 *
 * @param {Array<{ time: number, hr_value: number }>|null} points
 * @param {number} currentTimeSec
 * @param {{ videoDurationSec?: number }|number} [options]
 * @returns {number|null}
 */
export function hrAtVideoTime(points, currentTimeSec, options = {}) {
  if (!Array.isArray(points) || points.length === 0) return null;

  const playhead = Number(currentTimeSec);
  if (!Number.isFinite(playhead) || playhead < 0) return null;

  const opts =
    typeof options === "number" ? { videoDurationSec: 0 } : options || {};
  const videoDurationSec = Number(opts.videoDurationSec) || 0;

  const sorted = points
    .map((p) => ({
      time: Number(p?.time),
      hr: Number(p?.hr_value ?? p?.hr),
    }))
    .filter((p) => Number.isFinite(p.time) && Number.isFinite(p.hr) && p.hr > 0)
    .sort((a, b) => a.time - b.time);

  if (!sorted.length) return null;
  if (sorted.length === 1) return Math.round(sorted[0].hr);

  const tMin = sorted[0].time;
  const tMax = sorted[sorted.length - 1].time;
  const span = tMax - tMin;
  if (!(span > 0)) return Math.round(sorted[0].hr);

  // Prefer 1:1 with playhead on a clip-rebased series (t≈0).
  // If the series is much longer than the clip, map by video progress across
  // the series so the overlay still moves with the playhead.
  let target;
  const seriesLooksClipAligned =
    tMin <= 2 && (videoDurationSec <= 0.5 || span <= videoDurationSec * 2.5);
  if (seriesLooksClipAligned) {
    target = tMin + playhead;
  } else if (videoDurationSec > 0.5) {
    const progress = Math.min(1, Math.max(0, playhead / videoDurationSec));
    target = tMin + progress * span;
  } else {
    target = tMin + playhead;
  }

  const clampedTarget = Math.min(tMax, Math.max(tMin, target));

  let lo = 0;
  let hi = sorted.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (sorted[mid].time < clampedTarget) lo = mid + 1;
    else hi = mid;
  }
  const right = sorted[Math.min(sorted.length - 1, lo)];
  const left = sorted[Math.max(0, lo - 1)];

  if (left === right || right.time === left.time) {
    return Math.round(right.hr);
  }
  if (clampedTarget <= left.time) return Math.round(left.hr);
  if (clampedTarget >= right.time) return Math.round(right.hr);

  const ratio = (clampedTarget - left.time) / (right.time - left.time);
  return Math.round(left.hr + (right.hr - left.hr) * ratio);
}


